// Wikipedia prose for the loaded tune and its writers (issue #70, second slice).
//
// The mapping is a CURATED MANIFEST, not a live search (user ruling July 2026):
// `public/tune-knowledge.json` is built offline by `scripts/build-tune-knowledge.mjs`,
// which only records a page when the evidence is unambiguous — the article's
// title still carries the tune's name, its lead calls itself a piece of music,
// and the composer we credit is named in it. A tune with no verified entry shows
// no prose at all. That is the whole point: a jazz title is a magnet for wrong
// articles (albums it appeared on, films it was written for, the Portland bar
// named after it), and a plausible-looking wrong blurb is worse than none.
//
// At runtime we fetch only the SUMMARY of a page we already trust, from
// Wikipedia's REST endpoint (CORS-open, no key), and cache it for the session.

import { normalizeTuneText, tuneIdentity } from '@/music/tuneRelations';

export interface TuneKnowledgeEntry {
  title: string;
  composer: string;
  wikipedia: string;
  wikidata?: string;
}

export interface ComposerKnowledgeEntry {
  name: string;
  wikipedia: string;
  wikidata?: string;
}

export interface TuneKnowledgeManifest {
  generatedAt?: string;
  tunes: TuneKnowledgeEntry[];
  composers: ComposerKnowledgeEntry[];
  /**
   * A songwriting team written as a chain of surnames ("Rodgers-Hart") mapped to
   * the writer names it stands for. The builder proves each chain against the
   * writers the library resolved in full, so this is a lookup table, not a split
   * rule — the surnames are never taken apart at runtime.
   */
  teams?: Record<string, string[]>;
}

export interface KnowledgeIndex {
  byIdentity: Map<string, TuneKnowledgeEntry>;
  byTitle: Map<string, TuneKnowledgeEntry[]>;
  composers: Map<string, ComposerKnowledgeEntry>;
  teams: Map<string, ComposerKnowledgeEntry[]>;
}

/** A page summary, reduced to what the panel prints. */
export interface WikipediaSummary {
  title: string;
  extract: string;
  url: string;
  /**
   * Which of the article's sections the extract was quoted from, empty for the
   * lead. Provenance: it says whether the reader is getting "Chord changes" or
   * the opening paragraph.
   */
  section?: string;
  /** Wikidata's one-liner — "American jazz trumpeter", "1963 jazz standard". */
  description?: string;
  /**
   * The article's lead image — a writer's portrait, a tune's sheet or sleeve.
   * Served straight from Wikimedia and always shown next to a link back to the
   * article, which is also where the image's own credit and licence live.
   */
  image?: { source: string; width: number; height: number };
}

const WIKIPEDIA_REST = 'https://en.wikipedia.org/api/rest_v1/page/summary/';

/** The credits in "Miles Davis, Bill Evans" — the tune belongs to both writers. */
export function knowledgeCredits(composer: string | undefined): string[] {
  return (composer ?? '')
    .split(/[,&/]|\band\b/i)
    .map((name) => name.trim())
    .filter(Boolean);
}

function surname(credit: string): string {
  const words = normalizeTuneText(credit).split(' ').filter(Boolean);
  return words.at(-1) ?? '';
}

export function buildKnowledgeIndex(manifest: TuneKnowledgeManifest): KnowledgeIndex {
  const byIdentity = new Map<string, TuneKnowledgeEntry>();
  const byTitle = new Map<string, TuneKnowledgeEntry[]>();
  for (const tune of manifest.tunes ?? []) {
    byIdentity.set(tuneIdentity(tune.title, tune.composer), tune);
    const key = normalizeTuneText(tune.title);
    byTitle.set(key, [...(byTitle.get(key) ?? []), tune]);
  }
  const composers = new Map<string, ComposerKnowledgeEntry>();
  for (const writer of manifest.composers ?? []) {
    composers.set(normalizeTuneText(writer.name), writer);
  }
  const teams = new Map<string, ComposerKnowledgeEntry[]>();
  for (const [credit, names] of Object.entries(manifest.teams ?? {})) {
    const members = names
      .map((name) => composers.get(normalizeTuneText(name)))
      .filter((entry): entry is ComposerKnowledgeEntry => entry !== undefined);
    if (members.length === names.length) teams.set(normalizeTuneText(credit), members);
  }
  return { byIdentity, byTitle, composers, teams };
}

/**
 * The same tune reaches us credited differently by source ("Kenny Dorham" from
 * iReal, "K. Dorham" from an openbook header), so an identity miss falls back to
 * the title — but only when a writer's surname agrees, or when neither side
 * names one. Two different tunes DO share a title, and guessing between them
 * would print the wrong story with full confidence.
 */
export function lookupTuneEntry(
  index: KnowledgeIndex,
  title: string,
  composer: string | undefined,
): TuneKnowledgeEntry | undefined {
  const exact = index.byIdentity.get(tuneIdentity(title, composer));
  if (exact) return exact;
  const candidates = index.byTitle.get(normalizeTuneText(title)) ?? [];
  if (candidates.length === 0) return undefined;
  const names = knowledgeCredits(composer).map(surname).filter(Boolean);
  if (names.length === 0) return candidates.length === 1 ? candidates[0] : undefined;
  return candidates.find((entry) =>
    knowledgeCredits(entry.composer)
      .map(surname)
      .some((name) => names.includes(name)),
  );
}

/** Every credited writer we hold an article for, in the order they are credited. */
export function lookupComposerEntries(
  index: KnowledgeIndex,
  composer: string | undefined,
): ComposerKnowledgeEntry[] {
  const seen = new Set<string>();
  const found: ComposerKnowledgeEntry[] = [];
  for (const credit of knowledgeCredits(composer)) {
    const key = normalizeTuneText(credit);
    const entries = index.composers.has(key)
      ? [index.composers.get(key) as ComposerKnowledgeEntry]
      : (index.teams.get(key) ?? []);
    for (const entry of entries) {
      if (seen.has(entry.wikipedia)) continue;
      seen.add(entry.wikipedia);
      found.push(entry);
    }
  }
  return found;
}

let indexPromise: Promise<KnowledgeIndex> | null = null;

/** The manifest, fetched once per session; an empty index if it can't be read. */
export function tuneKnowledgeIndex(): Promise<KnowledgeIndex> {
  indexPromise ??= (async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}tune-knowledge.json`);
      if (res.ok) return buildKnowledgeIndex((await res.json()) as TuneKnowledgeManifest);
    } catch {
      /* fall through to the empty index — the panel simply shows no prose */
    }
    return buildKnowledgeIndex({ tunes: [], composers: [], teams: {} });
  })();
  return indexPromise;
}

/** One harvested paragraph, quoted from the article at build time. */
export interface TuneRecording {
  artist: string;
  work?: string;
  year?: number;
}

export interface TuneProseEntry {
  /** The heading it came from; empty means the lead, minus its definition. */
  section: string;
  text: string;
  /**
   * The article's own catalogue of versions to hear — parsed from the sections
   * the prose harvest rejects as lists. Absent when the article has none, or
   * none in a shape we can trust.
   */
  recordings?: TuneRecording[];
}

let prosePromise: Promise<Record<string, TuneProseEntry>> | null = null;

/**
 * The harvested prose, fetched once per session and keyed by the manifest's
 * article title. Built by `scripts/build-tune-prose.ts`: the REST summary a
 * page gives us is its LEAD, which is definitional by design and says what the
 * masthead already said — the paragraph a player wants is under the article's
 * own "Harmony" / "Musical characteristics" / "Authorship" heading, and reading
 * that means pulling the whole article, which is a build-time job.
 */
export function tuneProse(): Promise<Record<string, TuneProseEntry>> {
  prosePromise ??= (async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}tune-prose.json`);
      if (res.ok) {
        const data = (await res.json()) as { entries?: Record<string, TuneProseEntry> };
        return data.entries ?? {};
      }
    } catch {
      /* fall through — the panel prints the live summary's lead instead */
    }
    return {};
  })();
  return prosePromise;
}

const summaries = new Map<string, Promise<WikipediaSummary | null>>();

/**
 * How long to wait on Wikipedia. The paragraph is already on disk (see
 * `tuneProse`); this request only adds the lead image and Wikidata's one-liner,
 * so it must never be what the reader is waiting for. A network that HANGS
 * rather than fails — a captive portal, a filtered corporate DNS — would
 * otherwise keep the request open indefinitely.
 */
const SUMMARY_TIMEOUT_MS = 4000;

/** One page summary, cached per session. Resolves null when Wikipedia is unreachable. */
export function wikipediaSummary(page: string): Promise<WikipediaSummary | null> {
  const cached = summaries.get(page);
  if (cached) return cached;
  const request = (async (): Promise<WikipediaSummary | null> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SUMMARY_TIMEOUT_MS);
    try {
      const res = await fetch(
        `${WIKIPEDIA_REST}${encodeURIComponent(page.replace(/ /g, '_'))}?redirect=true`,
        { signal: controller.signal },
      );
      if (!res.ok) return null;
      const data = (await res.json()) as {
        type?: string;
        titles?: { normalized?: string };
        title?: string;
        extract?: string;
        description?: string;
        content_urls?: { desktop?: { page?: string } };
        thumbnail?: { source?: string; width?: number; height?: number };
      };
      // A disambiguation page reached through a manifest entry means the article
      // moved since the build; printing its list of options would be nonsense.
      if (data.type === 'disambiguation' || !data.extract) return null;
      const thumb = data.thumbnail;
      return {
        title: data.titles?.normalized ?? data.title ?? page,
        extract: data.extract,
        ...(data.description ? { description: data.description } : {}),
        url:
          data.content_urls?.desktop?.page ??
          `https://en.wikipedia.org/wiki/${encodeURIComponent(page.replace(/ /g, '_'))}`,
        ...(thumb?.source
          ? { image: { source: thumb.source, width: thumb.width ?? 0, height: thumb.height ?? 0 } }
          : {}),
      };
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })();
  summaries.set(page, request);
  // A null is almost always transient — the manifest only holds pages the
  // builder verified — so don't let one bad minute cost the picture for the
  // rest of the session. Reopening the drawer asks again.
  void request.then((summary) => {
    if (summary === null && summaries.get(page) === request) summaries.delete(page);
  });
  return request;
}

/**
 * Wikidata's one-liner carries the life: "American jazz trumpeter (1924–1972)",
 * "American jazz saxophonist (1933–2023)". This is where a writer's dates
 * actually come from — the REST summary's extract has ALREADY had the lead's
 * parenthetical dates removed, so reading them off the paragraph (see
 * `hoistLifespan`) only catches text from elsewhere.
 *
 * A bare year is left in the role text: "1963 instrumental by Kenny Dorham" is
 * a description of a tune, not somebody's dates.
 */
export function splitDescription(description?: string): { role?: string; dateline?: string } {
  if (!description) return {};
  const match = description.match(/^(.*?)\s*\((born\s+)?(\d{4})(?:\s*[–—-]\s*(\d{4}))?\)$/);
  if (!match) return { role: description };
  const [, role, born, from, to] = match;
  if (to) return { role, dateline: `${from}–${to}` };
  if (born) return { role, dateline: `b. ${from}` };
  return { role: description };
}

/**
 * Wikipedia opens a life with its dates in parentheses — "Kenny Dorham (August
 * 30, 1924 – December 5, 1972) was an American jazz trumpeter" — sometimes
 * behind a pronunciation guide. Hoisting them out gives the card a dateline and
 * leaves the paragraph reading as a sentence rather than a passport.
 *
 * Only a parenthesis that opens the lead is considered, and only when it reads
 * as dates: a bare year in a tune's title line ("Blue Bossa" (1963)) stays put,
 * because "1963" under a tune's name is not the same claim as a lifespan.
 */
export function hoistLifespan(extract: string): { dateline?: string; extract: string } {
  const open = extract.indexOf('(');
  if (open < 0 || open > 120) return { extract };
  const close = extract.indexOf(')', open);
  if (close < 0) return { extract };
  const inner = extract.slice(open + 1, close);
  const years = inner.match(/\b(1[5-9]\d{2}|20\d{2})\b/g) ?? [];
  const dateline =
    years.length >= 2 && /[–—-]/.test(inner)
      ? `${years[0]}–${years[years.length - 1]}`
      : years.length === 1 && /\bborn\b/i.test(inner)
        ? `b. ${years[0]}`
        : years.length === 1 && /\bdied\b/i.test(inner)
          ? `d. ${years[0]}`
          : null;
  if (!dateline) return { extract };
  const rest = `${extract.slice(0, open).trimEnd()}${extract.slice(close + 1)}`;
  return { dateline, extract: rest.replace(/\s+([,;.])/g, '$1').trim() };
}

/** Trim a lead to its first sentences — a drawer prints a paragraph, not a page. */
export function shortenExtract(extract: string, sentences = 2): string {
  const parts = extract.match(/[^.!?]+[.!?]+(?:["')\]]*)\s*/g);
  if (!parts) return extract.trim();
  const kept = parts.slice(0, sentences).join('').trim();
  return kept.length > 40 ? kept : parts.slice(0, sentences + 1).join('').trim();
}
